/* ==========================================================================
   Harper Reborn — cart extras (loaded on every page after cart-drawer.js)

   window.HP.cart  — the ONE place that talks to /cart/update.js for discount
                     codes. Used by the drawer / cart-page discount field and by
                     the product-page "click to copy" code block.
                     add(code, opts) / remove(code, opts) re-render the Dawn cart
                     sections (drawer AND cart page when both are on the page),
                     publish cartUpdate and announce the result in the cart live
                     region. A code Shopify reports as not applicable is rolled
                     back to the original code set and the UI is re-rendered.
   <hp-cart-discount> — discount input + removable chips (snippets/hp-cart-discount.liquid)
   <hp-cart-timer>    — drawer countdown; deadline kept in sessionStorage so
                        re-renders don't reset it, never restarted after expiry
   [data-hp-upsell-nav="prev|next"] — arrows for the upsell strip (ara-bundle)
   [data-hp-upsell-select]           — upsell variant select → price sync

   Volume discounts are Shopify automatic discounts (see hp-quantity-breaks.js);
   nothing is queued here any more.

   Depends on Dawn globals: routes, fetchConfig, publish/subscribe, PUB_SUB_EVENTS,
   trapFocus, and the CartItems / CartDrawerItems custom elements.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;

  function ss(key, val) {
    try {
      if (val === undefined) return window.sessionStorage.getItem(key) || '';
      if (val === '' || val === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, val);
    } catch (e) {
      return '';
    }
    return val;
  }

  function norm(code) {
    return String(code || '').trim().toUpperCase();
  }

  function uniq(list) {
    var seen = {};
    return list.filter(function (c) {
      if (!c || seen[c]) return false;
      seen[c] = true;
      return true;
    });
  }

  function sectionInnerHTML(html, selector) {
    if (typeof html !== 'string' || html === '') return null;
    var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
    return parsed ? parsed.innerHTML : null;
  }

  function parseJson(r) {
    return r
      .text()
      .then(function (t) {
        try {
          return t ? JSON.parse(t) : {};
        } catch (e) {
          return {};
        }
      })
      .then(function (json) {
        if (!r.ok) {
          var err = new Error(json.description || json.message || 'cart ' + r.status);
          err.status = r.status;
          err.json = json;
          throw err;
        }
        return json;
      });
  }

  var cart = {
    /** Every Dawn cart element on the page (drawer and/or cart page). */
    hosts: function () {
      return [].slice.call(doc.querySelectorAll('cart-drawer-items, cart-items'));
    },

    /** Union of the hosts' getSectionsToRender() lists, de-duplicated by section. */
    sections: function (preferred) {
      var list = [];
      var seen = {};
      var hosts = cart.hosts();
      if (preferred && hosts.indexOf(preferred) > 0) {
        hosts.splice(hosts.indexOf(preferred), 1);
        hosts.unshift(preferred);
      }
      hosts.forEach(function (host) {
        if (typeof host.getSectionsToRender !== 'function') return;
        var secs;
        try {
          secs = host.getSectionsToRender();
        } catch (e) {
          return;
        }
        (secs || []).forEach(function (s) {
          if (!s || !s.section || seen[s.section + '|' + s.id]) return;
          seen[s.section + '|' + s.id] = true;
          list.push(s);
        });
      });
      return list.length ? list : null;
    },

    /** POST /cart/update.js with the full set of codes ([] clears). Resolves with the cart JSON. */
    post: function (codes, withSections) {
      var body = { discount: uniq((codes || []).map(norm)).join(',') };
      var secs = withSections ? cart.sections() : null;
      if (secs) {
        body.sections = uniq(secs.map(function (s) { return s.section; })).join(',');
        body.sections_url = window.location.pathname;
      }
      var cfg = typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      cfg.body = JSON.stringify(body);
      return fetch(window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update.js', cfg).then(parseJson);
    },

    /** Current cart JSON (GET /cart.js). */
    fetch: function () {
      return fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(parseJson);
    },

    codesOf: function (json) {
      return uniq(((json && json.discount_codes) || []).map(function (d) {
        return norm(d.code);
      }));
    },

    isApplicable: function (json, code) {
      code = norm(code);
      var hit = ((json && json.discount_codes) || []).filter(function (d) {
        return norm(d.code) === code;
      })[0];
      return !!(hit && hit.applicable !== false);
    },

    /** Paint the rendered sections from an update response (same loop as Dawn's cart.js). */
    render: function (json) {
      var secs = cart.sections();
      if (!secs || !json || !json.sections) return false;
      secs.forEach(function (s) {
        var root = doc.getElementById(s.id);
        if (!root) return;
        var target = root.querySelector(s.selector) || root;
        var html = sectionInnerHTML(json.sections[s.section], s.selector);
        if (html !== null) target.innerHTML = html;
      });
      var empty = json.item_count === 0;
      cart.hosts().forEach(function (h) { h.classList.toggle('is-empty', empty); });
      var drawer = doc.querySelector('cart-drawer');
      var footer = doc.getElementById('main-cart-footer');
      if (drawer) drawer.classList.toggle('is-empty', empty);
      if (footer) footer.classList.toggle('is-empty', empty);
      return true;
    },

    notify: function (json) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // 'cart-items' as source keeps CartItems from doing a redundant second fetch.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: json });
      }
    },

    /** Write a message into the cart live region(s) (Dawn's pattern: announce, then clear). */
    announce: function (text) {
      if (!text) return;
      ['CartDrawer-LiveRegionText', 'cart-live-region-text'].forEach(function (id) {
        var el = doc.getElementById(id);
        if (!el) return;
        el.textContent = text;
        el.setAttribute('aria-hidden', 'false');
        setTimeout(function () {
          if (el.textContent === text) el.textContent = '';
          el.setAttribute('aria-hidden', 'true');
        }, 3000);
      });
    },

    /** Re-arm Dawn's focus trap after the drawer was re-painted. */
    refocus: function (preferSelector) {
      var drawer = doc.querySelector('cart-drawer');
      var fresh = preferSelector ? doc.querySelector(preferSelector) : null;
      if (drawer && drawer.classList.contains('active')) {
        if (typeof trapFocus === 'function') trapFocus(drawer, fresh || drawer.querySelector('.drawer__inner'));
        else if (fresh) fresh.focus();
      } else if (fresh) {
        fresh.focus();
      }
    },

    /**
     * Add one code on top of whatever is already on the cart.
     * Resolves with { ok, cart, reason } where reason ∈ 'invalid' | 'combine'.
     * A code Shopify reports as not applicable is rolled back to the ORIGINAL
     * code set and the cart UI is re-rendered so nothing stale stays on screen.
     */
    add: function (code, opts) {
      opts = opts || {};
      code = norm(code);
      if (!code) return Promise.resolve({ ok: false, reason: 'invalid' });
      return cart.fetch().then(function (current) {
        var original = cart.codesOf(current);
        if (original.indexOf(code) > -1) return { ok: true, cart: current, already: true };
        return cart.post(original.concat([code]), true).then(function (json) {
          if (cart.isApplicable(json, code)) {
            if (opts.render !== false) {
              cart.render(json);
              cart.notify(json);
            }
            return { ok: true, cart: json };
          }
          // Not applicable. Find out whether it is the code itself or the combination.
          var probe = original.length ? cart.post([code], false) : Promise.resolve(null);
          return probe.then(function (alone) {
            var reason = alone && cart.isApplicable(alone, code) ? 'combine' : 'invalid';
            return cart.post(original, true).then(function (rolled) {
              if (opts.render !== false) {
                cart.render(rolled);
                cart.notify(rolled);
              }
              return { ok: false, cart: rolled, reason: reason };
            });
          });
        });
      });
    },

    /** Remove one code (leaves every other code untouched). */
    remove: function (code, opts) {
      opts = opts || {};
      code = norm(code);
      return cart.fetch().then(function (current) {
        var rest = cart.codesOf(current).filter(function (c) {
          return c !== code;
        });
        return cart.post(rest, opts.render !== false).then(function (json) {
          if (opts.render !== false) {
            cart.render(json);
            cart.notify(json);
          }
          return { ok: true, cart: json };
        });
      });
    }
  };

  window.HP = window.HP || {};
  window.HP.cart = cart;

  /* ---------------- <hp-cart-discount> ---------------- */
  var pending = null; // typed value survives the drawer's innerHTML swaps

  class HpCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-hp-discount-form]');
      this.input = this.querySelector('input');
      this.error = this.querySelector('[data-hp-discount-error]');
      this.details = this.querySelector('details');
      this.codes = (this.dataset.codes || '').split(',').map(norm).filter(Boolean);
      this.msg = {
        invalid: this.dataset.msgInvalid || (this.error ? this.error.textContent.trim() : ''),
        combine: this.dataset.msgCombine || this.dataset.msgInvalid || '',
        error: this.dataset.msgError || (window.cartStrings && window.cartStrings.error) || '',
        applied: this.dataset.msgApplied || '',
        removed: this.dataset.msgRemoved || ''
      };
      if (pending && Date.now() - pending.t < 10000) {
        if (this.input) this.input.value = pending.value;
        if (this.details && pending.open) this.details.open = true;
        if (this.error && pending.error) this.showError(pending.errorText);
      }
      pending = null;
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.apply(this.input ? this.input.value : '');
        });
      }
      if (this.input) {
        this.input.addEventListener('input', () => this.hideError());
      }
      this.querySelectorAll('[data-hp-remove-code]').forEach((btn) => {
        btn.addEventListener('click', () => this.removeCode(btn.dataset.hpRemoveCode));
      });
    }

    disconnectedCallback() {
      pending = {
        value: this.input ? this.input.value : '',
        open: !!(this.details && this.details.open),
        error: !!(this.error && !this.error.hidden),
        errorText: this.error ? this.error.textContent : '',
        t: Date.now()
      };
    }

    /** The Dawn element this field belongs to (drawer vs cart page). */
    host() {
      var byContext = this.dataset.context === 'page' ? doc.querySelector('cart-items') : doc.querySelector('cart-drawer-items');
      return this.closest('cart-items, cart-drawer-items') || byContext || cart.hosts()[0] || null;
    }

    freshSelector() {
      var ctx = this.dataset.context || 'drawer';
      return 'hp-cart-discount[data-context="' + ctx + '"] input, hp-cart-discount[data-context="' + ctx + '"] summary';
    }

    busy(on) {
      this.classList.toggle('is-busy', !!on);
      this.querySelectorAll('button, input').forEach((el) => {
        el.disabled = !!on;
      });
    }

    showError(msg) {
      if (!this.error) return;
      if (msg) this.error.textContent = msg;
      this.error.hidden = false;
      this.classList.add('hp-disc--error');
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.classList.remove('hp-disc--error');
      if (this.input) this.input.removeAttribute('aria-invalid');
    }

    apply(raw) {
      var code = norm(raw);
      if (!code || this.codes.indexOf(code) > -1) return;
      if (doc.querySelector('.cart__items--disabled')) return; // a quantity request is in flight
      this.busy(true);
      this.hideError();
      var sel = this.freshSelector();
      cart
        .add(code)
        .then((res) => {
          // The element may or may not have been replaced by cart.render(); release both.
          this.busy(false);
          var fresh = doc.querySelector('hp-cart-discount[data-context="' + (this.dataset.context || 'drawer') + '"]');
          if (fresh && fresh !== this) fresh.busy(false);
          var target = fresh || this;
          if (!res.ok) {
            target.showError(res.reason === 'combine' ? target.msg.combine : target.msg.invalid);
            if (target.input) target.input.value = raw;
            if (target.details) target.details.open = true;
            cart.refocus('hp-cart-discount[data-context="' + (this.dataset.context || 'drawer') + '"] input');
            return;
          }
          cart.announce(target.msg.applied);
          cart.refocus(sel);
        })
        .catch(() => {
          this.busy(false);
          var fresh = doc.querySelector('hp-cart-discount[data-context="' + (this.dataset.context || 'drawer') + '"]');
          (fresh || this).showError((fresh || this).msg.error);
        });
    }

    removeCode(code) {
      this.busy(true);
      var ctx = this.dataset.context || 'drawer';
      var msg = this.msg;
      cart
        .remove(code)
        .then(() => {
          this.busy(false);
          var fresh = doc.querySelector('hp-cart-discount[data-context="' + ctx + '"]');
          if (fresh && fresh !== this) fresh.busy(false);
          cart.announce(msg.removed);
          cart.refocus('hp-cart-discount[data-context="' + ctx + '"] summary');
        })
        .catch(() => {
          this.busy(false);
          this.showError(msg.error);
        });
    }
  }
  if (!customElements.get('hp-cart-discount')) customElements.define('hp-cart-discount', HpCartDiscount);

  /* ---------------- <hp-cart-timer> ---------------- */
  var T_KEY = 'hp_cart_timer_deadline';

  class HpCartTimer extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-hp-timer-value]') || this;
      var mins = parseInt(this.dataset.minutes || '12', 10);
      var secs = parseInt(this.dataset.seconds || '59', 10);
      var deadline = parseInt(ss(T_KEY) || '0', 10);
      if (!deadline || isNaN(deadline)) {
        // First time this session: arm it. An expired deadline stays expired (no restart).
        deadline = Date.now() + (mins * 60 + secs) * 1000;
        ss(T_KEY, String(deadline));
      }
      this.deadline = deadline;
      this.tick();
      if (this.deadline > Date.now()) this.iv = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.iv) clearInterval(this.iv);
    }

    tick() {
      var left = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
      var m = Math.floor(left / 60);
      var s = left % 60;
      this.out.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      if (left <= 0) {
        if (this.iv) clearInterval(this.iv);
        this.iv = null;
        this.classList.add('is-done');
      }
    }
  }
  if (!customElements.get('hp-cart-timer')) customElements.define('hp-cart-timer', HpCartTimer);

  // Reset the timer when the cart empties so the next session starts fresh.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cartData && event.cartData.item_count === 0) ss(T_KEY, '');
    });
  }

  /* ---------------- upsell strip: arrows + variant price sync ---------------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-hp-upsell-nav]');
    if (!btn) return;
    var strip = btn.closest('[data-hp-upsell]');
    strip = strip && strip.querySelector('[data-hp-upsell-strip]');
    if (!strip) return;
    var dir = btn.dataset.hpUpsellNav === 'prev' ? -1 : 1;
    strip.scrollBy({ left: dir * strip.clientWidth * 0.85, behavior: 'smooth' });
  });

  doc.addEventListener('change', function (e) {
    var select = e.target.closest && e.target.closest('[data-hp-upsell-select]');
    if (!select) return;
    var item = select.closest('.hp-upsell__item');
    var opt = select.options[select.selectedIndex];
    if (!item || !opt) return;
    var price = item.querySelector('[data-hp-upsell-price]');
    var compare = item.querySelector('[data-hp-upsell-compare]');
    var btn = item.querySelector('.product-form__submit');
    if (price && opt.dataset.priceText) price.textContent = opt.dataset.priceText;
    if (compare) {
      compare.hidden = !opt.dataset.compareText;
      compare.textContent = opt.dataset.compareText || '';
    }
    if (btn) btn.disabled = opt.disabled;
  });
})();
